import { useEffect, useMemo, useRef, useState } from "react";
import {
  createReference,
  deleteReference,
  getUploadUrl,
  listAllReferenceOwners,
  listReferences,
  listReferencesByUser,
  listUsers,
  markReferenceProvided,
  renameReference,
  setReferenceDefaults,
  triggerVoicePreview,
  updateReference,
  uploadFile,
} from "../lib/api";
import type { Reference, VoiceDefaults } from "../lib/types";
import { ParamSlider } from "../components/ParamSlider";
import { useAuth } from "../hooks/useAuth";
import { useDataCache } from "../contexts/DataCacheContext";
import { AudioRecorder } from "../components/AudioRecorder";
import { WaveformTrimmer } from "../components/WaveformTrimmer";
import { TauriYouTubeImport } from "../components/TauriYouTubeImport";
import { isTauri } from "../lib/tauri";
import { downloadUrlAs, saveBlob, trimAudio } from "../lib/audioUtils";
import { Icon } from "../components/icons";
import { ImageEditor, type ImageEditorResult } from "../components/ImageEditor";
import {
  VoicePreviewPlayer,
  GeneratePreviewButton,
} from "../components/VoicePreviewPlayer";

type FilterKey = "all" | "provided" | "mine";

type AdminRef = Reference & { userId: string; provided?: boolean };

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/** Voice name → a filename that survives every OS. */
function toFilename(name: string): string {
  const safe = name.replace(/[^\w.\-() ]+/g, "_").replace(/\s+/g, " ").trim();
  return safe || "voice";
}

/** Carry the original upload's container through to the downloaded file. */
function originalExtension(s3Key: string): string {
  const match = /\.([A-Za-z0-9]{1,5})$/.exec(s3Key);
  return match ? `.${match[1].toLowerCase()}` : ".wav";
}

/**
 * Length of the saved default clip, or null when no default has been saved and
 * the full original is what generation uses. Legacy rows carry trim bounds
 * without a `defaultClipDuration`, so fall back to the bounds.
 */
function savedClipDuration(ref: Reference): number | null {
  if (ref.defaultClipDuration != null) return ref.defaultClipDuration;
  if (ref.defaultTrimStart != null && ref.defaultTrimEnd != null) {
    return ref.defaultTrimEnd - ref.defaultTrimStart;
  }
  return null;
}

function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.addEventListener("loadedmetadata", () => {
      resolve(audio.duration);
      URL.revokeObjectURL(audio.src);
    });
    audio.addEventListener("error", reject);
    audio.src = URL.createObjectURL(file);
  });
}

export function Library() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const cache = useDataCache();

  const [references, setReferences] = useState<Reference[]>(cache.references);
  const [providedRefs, setProvidedRefs] = useState<Reference[]>(cache.providedRefs);
  const [refOwners, setRefOwners] = useState<Array<{ userId: string; count: number }>>([]);
  const [refsByUser, setRefsByUser] = useState<Record<string, AdminRef[]>>({});
  const [loadingRefsFor, setLoadingRefsFor] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(cache.refsLoading && cache.references.length === 0);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [youtubeImport, setYoutubeImport] = useState(false);
  const [showAllRefs, setShowAllRefs] = useState(false);
  const [refUserMap, setRefUserMap] = useState<Map<string, string>>(new Map());
  const [expandedRefGroups, setExpandedRefGroups] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [playingName, setPlayingName] = useState<string | null>(null);
  const [previewPlaying, setPreviewPlaying] = useState<string | null>(null);
  const [previewProgress, setPreviewProgress] = useState<Record<string, number>>({});
  const [retryingPreview, setRetryingPreview] = useState<Set<string>>(new Set());
  const audioRef = useRef<HTMLAudioElement>(null);
  const previewAudios = useRef<Map<string, HTMLAudioElement | null>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");

  const [trimmingRef, setTrimmingRef] = useState<string | null>(null);
  const [trimRange, setTrimRange] = useState<{ start: number; end: number } | null>(null);
  const [savingTrim, setSavingTrim] = useState(false);

  const [renamingRef, setRenamingRef] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);

  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  // Card key of the reference currently being fetched for download, so the
  // button can show a spinner instead of silently doing nothing on a long clip.
  const [downloadingRef, setDownloadingRef] = useState<string | null>(null);

  // Full-modal image editor (paste URL or file upload → square crop with zoom).
  // We track the *name* of the ref being edited so we can look up its current
  // original/crop on render — the snapshot in `references` is always fresh.
  const [editingImageRef, setEditingImageRef] = useState<string | null>(null);

  const [defaultsTarget, setDefaultsTarget] = useState<{
    userId: string;
    name: string;
    current: VoiceDefaults | undefined;
  } | null>(null);
  const [defaultsForm, setDefaultsForm] = useState<VoiceDefaults>({});
  const [savingDefaults, setSavingDefaults] = useState(false);

  const [uploadingRef, setUploadingRef] = useState<{ name: string; duration: number } | null>(null);

  useEffect(() => {
    if (cache.references.length > 0 || cache.providedRefs.length > 0) {
      setReferences(cache.references);
      setProvidedRefs(cache.providedRefs);
      setLoading(false);
    }
    void loadRefs();
  }, []);

  async function loadRefs() {
    try {
      const result = await listReferences();
      setReferences(result.items);
      setProvidedRefs(result.provided ?? []);
      cache.refreshRefs();
    } catch {
      setError("Failed to load references");
    } finally {
      setLoading(false);
    }
  }

  async function loadAllRefs() {
    try {
      const map = new Map<string, string>();
      const collectUsers = (async () => {
        let cursor: string | undefined;
        do {
          const page = await listUsers(100, cursor);
          for (const u of page.items) map.set(u.userId, u.email);
          cursor = page.cursor;
        } while (cursor);
      })();
      const [ownersResult] = await Promise.all([
        listAllReferenceOwners(),
        collectUsers,
      ]);
      setRefUserMap(map);
      const others = ownersResult.owners.filter((o) => o.userId !== user?.userId);
      setRefOwners(others);
    } catch {
      setError("Failed to load all references");
    }
  }

  async function loadRefsForUser(userId: string, force = false) {
    if (!force && (refsByUser[userId] || loadingRefsFor.has(userId))) return;
    setLoadingRefsFor((prev) => new Set(prev).add(userId));
    try {
      const result = await listReferencesByUser(userId);
      setRefsByUser((prev) => ({ ...prev, [userId]: result.items as AdminRef[] }));
    } catch {
      setError("Failed to load references for that user");
    } finally {
      setLoadingRefsFor((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadAudioFile(file, file.name.replace(/\.[^.]+$/, ""));
  };

  const handleRecorded = async (blob: Blob, duration: number) => {
    setRecording(false);
    const name = `Recording ${new Date().toLocaleString().replace(/\//g, "-")}`;
    const file = new File([blob], `${name}.wav`, { type: blob.type || "audio/wav" });
    await uploadAudioFile(file, name, duration);
  };

  async function uploadAudioFile(file: File | Blob, name: string, knownDuration?: number) {
    setUploading(true);
    setError(null);
    const duration = knownDuration ?? (file instanceof File ? await getAudioDuration(file) : 0);
    setUploadingRef({ name, duration });
    try {
      const contentType = file.type || "audio/wav";
      const filename = file instanceof File ? file.name : `${name}.wav`;
      const { uploadUrl, key } = await getUploadUrl("reference", contentType, filename);
      await uploadFile(uploadUrl, file, contentType);
      await createReference({ name, s3Key: key, duration });
      await loadRefs();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploadingRef(null);
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const handleDelete = async (name: string) => {
    if (confirmingDelete !== name) {
      setConfirmingDelete(name);
      return;
    }
    setConfirmingDelete(null);
    const prev = references;
    setReferences((r) => r.filter((ref) => ref.name !== name));
    try {
      await deleteReference(name);
    } catch (err) {
      setReferences(prev);
      setError(typeof err === "string" ? err : err instanceof Error ? err.message : "Failed to delete");
    }
  };

  const handlePlay = (ref: Reference) => {
    if (!audioRef.current || !ref.previewUrl) return;
    // Pause the voice-preview if it's playing — only one audio at a time.
    if (previewPlaying) {
      previewAudios.current.get(previewPlaying)?.pause();
      setPreviewPlaying(null);
    }
    if (playingName === ref.name) {
      audioRef.current.pause();
      setPlayingName(null);
    } else {
      audioRef.current.src = ref.previewUrl;
      void audioRef.current.play();
      setPlayingName(ref.name);
    }
  };

  const handleGeneratePreview = async (ref: Reference) => {
    const isCrossUser = !!ref.provided && !!ref.ownerUserId;
    if (isCrossUser) return;
    const key = `${ref.name}::${ref.s3Key}`;
    if (retryingPreview.has(key)) return;
    setRetryingPreview((s) => new Set(s).add(key));
    try {
      await triggerVoicePreview(ref.name);
      await loadRefs();
    } catch (err) {
      console.error("Failed to trigger voice preview:", err);
      setError(err instanceof Error ? err.message : "Failed to trigger voice preview");
    } finally {
      setRetryingPreview((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  };

  const handleVoicePreviewPlay = (ref: Reference) => {
    const target = previewAudios.current.get(ref.name);
    if (!target || !ref.voicePreviewUrl) return;
    // Pause the source player if it's playing — one audio at a time.
    if (audioRef.current && playingName) {
      audioRef.current.pause();
      setPlayingName(null);
    }
    if (previewPlaying === ref.name) {
      target.pause();
      setPreviewPlaying(null);
      return;
    }
    if (previewPlaying) {
      previewAudios.current.get(previewPlaying)?.pause();
    }
    void target.play();
    setPreviewPlaying(ref.name);
  };

  const handleVoicePreviewSeek = (ref: Reference, fraction: number) => {
    const target = previewAudios.current.get(ref.name);
    if (!target) return;
    const clamped = Math.max(0, Math.min(1, fraction));
    if (target.duration && Number.isFinite(target.duration)) {
      target.currentTime = clamped * target.duration;
    }
    setPreviewProgress((prev) => ({ ...prev, [ref.name]: clamped }));
    if (previewPlaying !== ref.name) {
      if (previewPlaying) {
        previewAudios.current.get(previewPlaying)?.pause();
      }
      if (audioRef.current && playingName) {
        audioRef.current.pause();
        setPlayingName(null);
      }
      void target.play();
      setPreviewPlaying(ref.name);
    }
  };

  const handleToggleProvided = async (
    userId: string,
    name: string,
    currentlyProvided: boolean
  ) => {
    setError(null);
    try {
      await markReferenceProvided(userId, name, !currentlyProvided);
      setRefsByUser((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      if (showAllRefs) {
        const email = refUserMap.get(userId) ?? userId;
        if (expandedRefGroups.has(email)) await loadRefsForUser(userId, true);
      }
      await loadRefs();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update provided status");
    }
  };

  const canTrimRef = (ref: Reference): boolean => {
    if (isAdmin) return true;
    if (ref.provided) return false;
    return true;
  };
  const canTrimProvidedRef = (_ref: Reference): boolean => isAdmin;

  const handleStartTrim = (key: string) => {
    if (trimmingRef === key) {
      setTrimmingRef(null);
      setTrimRange(null);
    } else {
      setTrimmingRef(key);
      setTrimRange(null);
    }
  };
  const handleTrimUpdate = (start: number, end: number) => setTrimRange({ start, end });

  const handleSaveTrim = async (ref: Reference) => {
    if (!trimRange || !ref.previewUrl) return;
    const trimmedDuration = trimRange.end - trimRange.start;
    if (trimmedDuration <= 0) return;
    if (trimRange.start < 0.05 && Math.abs(trimRange.end - ref.duration) < 0.05) {
      setTrimmingRef(null);
      setTrimRange(null);
      return;
    }
    setSavingTrim(true);
    setError(null);
    try {
      const trimmedBlob = await trimAudio(ref.previewUrl, trimRange.start, trimRange.end);
      const contentType = "audio/wav";
      const filename = `${ref.name}-trimmed.wav`;
      const { uploadUrl, key } = await getUploadUrl("reference", contentType, filename);
      await uploadFile(uploadUrl, trimmedBlob, contentType);
      await updateReference(ref.name, { s3Key: key, duration: trimmedDuration });
      await loadRefs();
      setTrimmingRef(null);
      setTrimRange(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save trim");
    } finally {
      setSavingTrim(false);
    }
  };

  /**
   * Download the reference's audio exactly as generation consumes it: the saved
   * default clip when one exists, otherwise the whole original. A five-minute
   * upload trimmed to a sixty-second default downloads as sixty seconds — not
   * the five-minute source.
   */
  const handleDownloadRef = async (ref: Reference, cardKey: string) => {
    if (downloadingRef) return;
    setDownloadingRef(cardKey);
    setError(null);
    const base = toFilename(ref.name);
    try {
      if (ref.defaultClipPreviewUrl) {
        // Server-extracted clip — already cut to (defaultTrimStart, defaultTrimEnd)
        // and always written as WAV by the extract Lambda.
        await downloadUrlAs(ref.defaultClipPreviewUrl, `${base}.wav`);
      } else if (
        ref.previewUrl &&
        ref.defaultTrimStart != null &&
        ref.defaultTrimEnd != null
      ) {
        // Legacy row: trim bounds saved but no pre-extracted clip. Cut it
        // client-side so the file still matches what generation would use.
        const clip = await trimAudio(ref.previewUrl, ref.defaultTrimStart, ref.defaultTrimEnd);
        saveBlob(clip, `${base}.wav`);
      } else if (ref.previewUrl) {
        // No default saved — the full original is the effective clip.
        await downloadUrlAs(ref.previewUrl, `${base}${originalExtension(ref.s3Key)}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to download");
    } finally {
      setDownloadingRef(null);
    }
  };

  const handleStartRename = (ref: Reference) => {
    setRenamingRef(ref.name);
    setRenameValue(ref.name);
  };
  const handleCancelRename = () => {
    setRenamingRef(null);
    setRenameValue("");
  };
  const handleSaveRename = async (ref: Reference, ownerUserId?: string) => {
    const newName = renameValue.trim();
    if (!newName || newName === ref.name) {
      handleCancelRename();
      return;
    }
    setSavingRename(true);
    setError(null);
    try {
      await renameReference(ref.name, newName, ownerUserId);
      await loadRefs();
      if (ownerUserId && ownerUserId !== user?.userId) {
        setRefsByUser((prev) => {
          const next = { ...prev };
          delete next[ownerUserId];
          return next;
        });
        const email = refUserMap.get(ownerUserId) ?? ownerUserId;
        if (showAllRefs && expandedRefGroups.has(email)) {
          await loadRefsForUser(ownerUserId, true);
        }
      }
      handleCancelRename();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rename");
    } finally {
      setSavingRename(false);
    }
  };
  const handleRenameKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    ref: Reference,
    ownerUserId?: string
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSaveRename(ref, ownerUserId);
    } else if (e.key === "Escape") {
      handleCancelRename();
    }
  };

  const handleEditImage = (ref: Reference) => setEditingImageRef(ref.name);
  const handleCancelImage = () => setEditingImageRef(null);

  /**
   * Optimistic save: close the modal as soon as the cropper hands back the
   * rendered blob, then run the S3 upload + DDB write + refresh in the
   * background. The card keeps showing the previous image until loadRefs
   * returns; if anything fails we surface it via the error banner.
   */
  const handleImageEditorSaved = (ref: Reference, result: ImageEditorResult) => {
    setEditingImageRef(null);
    setError(null);
    void (async () => {
      try {
        const { uploadUrl, key } = await getUploadUrl(
          "reference-image",
          result.contentType,
          "crop.jpg",
        );
        await uploadFile(uploadUrl, result.blob, result.contentType);
        await updateReference(ref.name, {
          s3Key: ref.s3Key,
          duration: ref.duration,
          imageUrl: key,
          // originalKey is "" when re-cropping an existing original — leave
          // imageOriginalS3Key absent so the server keeps the persisted value.
          ...(result.originalKey ? { imageOriginalS3Key: result.originalKey } : {}),
          imageCrop: result.crop,
        });
        await loadRefs();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to save image");
      }
    })();
  };

  const handleOpenDefaults = (
    userId: string,
    name: string,
    current: VoiceDefaults | undefined
  ) => {
    setDefaultsTarget({ userId, name, current });
    setDefaultsForm(
      current ?? {
        exaggeration: 0.5,
        cfgWeight: 0.5,
        temperature: 0.8,
        repetitionPenalty: 1.1,
        topP: 0.95,
        minP: 0.05,
      }
    );
  };
  const handleSaveDefaults = async () => {
    if (!defaultsTarget) return;
    setSavingDefaults(true);
    setError(null);
    try {
      await setReferenceDefaults(defaultsTarget.userId, defaultsTarget.name, defaultsForm);
      await invalidateAdminUserSlice(defaultsTarget.userId);
      await loadRefs();
      setDefaultsTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save defaults");
    } finally {
      setSavingDefaults(false);
    }
  };
  const handleClearDefaults = async () => {
    if (!defaultsTarget) return;
    setSavingDefaults(true);
    setError(null);
    try {
      await setReferenceDefaults(defaultsTarget.userId, defaultsTarget.name, null);
      await invalidateAdminUserSlice(defaultsTarget.userId);
      await loadRefs();
      setDefaultsTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to clear defaults");
    } finally {
      setSavingDefaults(false);
    }
  };

  async function invalidateAdminUserSlice(userId: string) {
    setRefsByUser((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    const email = refUserMap.get(userId) ?? userId;
    if (showAllRefs && expandedRefGroups.has(email)) {
      await loadRefsForUser(userId, true);
    }
  }

  const refOwnersDisplay: Array<{ email: string; userId: string; count: number }> = refOwners
    .map(({ userId, count }) => ({
      email: refUserMap.get(userId) ?? userId,
      userId,
      count,
    }))
    .sort((a, b) => a.email.localeCompare(b.email));

  const toggleRefGroup = (email: string, userId: string) => {
    const willExpand = !expandedRefGroups.has(email);
    setExpandedRefGroups((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
    if (willExpand) void loadRefsForUser(userId);
  };

  // Combine provided + own refs into a single grid stream, with provenance flag.
  const allCards = useMemo(() => {
    const provided = providedRefs.map((r) => ({ ...r, _isProvided: true, _own: false } as Reference & { _isProvided: boolean; _own: boolean }));
    const own = references.map(
      (r) => ({ ...r, _isProvided: r.provided ?? false, _own: true } as Reference & { _isProvided: boolean; _own: boolean })
    );
    return [...provided, ...own];
  }, [providedRefs, references]);

  const visibleCards = useMemo(
    () =>
      allCards.filter((v) => {
        if (filter === "provided" && !v._isProvided) return false;
        if (filter === "mine" && v._isProvided) return false;
        if (search && !v.name.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      }),
    [allCards, filter, search]
  );

  return (
    <div className="content">
      <div className="ph">
        <div>
          <div className="ph-eyebrow">Voice Library</div>
          <h1 className="ph-title">Your voice references</h1>
          <div className="ph-lede">
            {allCards.length} voices total · {providedRefs.length} curated ·{" "}
            {references.length} yours. Ten clean seconds is enough to clone a voice.
          </div>
        </div>
        <div className="ph-actions">
          <button
            type="button"
            className={`btn ${recording ? "btn-line" : "btn-ghost"}`}
            onClick={() => {
              setRecording(!recording);
              setYoutubeImport(false);
            }}
            title="Record"
          >
            <Icon.Mic /> Record
          </button>
          <label className="btn btn-line" style={{ cursor: "pointer" }} title="Upload audio">
            <Icon.Up /> Upload
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={handleUpload}
              style={{ display: "none" }}
              disabled={uploading}
            />
          </label>
          {isTauri() && (
            <button
              type="button"
              className={`btn ${youtubeImport ? "btn-line" : "btn-ghost"}`}
              onClick={() => {
                setYoutubeImport(!youtubeImport);
                setRecording(false);
              }}
              title="Import from YouTube"
            >
              <Icon.YouTube /> YouTube
            </button>
          )}
        </div>
      </div>

      {error && <div className="err-banner">{error}</div>}

      {recording && (
        <div style={{ marginBottom: 18 }}>
          <AudioRecorder onRecorded={handleRecorded} />
        </div>
      )}

      {youtubeImport && isTauri() && (
        <div style={{ marginBottom: 18 }}>
          <TauriYouTubeImport
            onComplete={() => {
              setYoutubeImport(false);
              loadRefs();
            }}
            onCancel={() => setYoutubeImport(false)}
          />
        </div>
      )}

      <audio ref={audioRef} onEnded={() => setPlayingName(null)} preload="none" />

      <div className="voice-tools">
        <div className="voice-search">
          <Icon.Search />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search references…"
          />
        </div>
        <div className="voice-filter">
          {(
            [
              ["all", "All"],
              ["provided", "Curated"],
              ["mine", "Yours"],
            ] as const
          ).map(([k, l]) => (
            <button
              key={k}
              type="button"
              className={filter === k ? "on" : ""}
              onClick={() => setFilter(k)}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="lib-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="skeleton-card" style={{ height: 160 }} />
          ))}
        </div>
      ) : visibleCards.length === 0 && !uploadingRef ? (
        <div className="empty-state">
          <div className="ico"><Icon.Library /></div>
          <h3>No voice references yet</h3>
          <p>Upload a clip, record your voice, or import from YouTube to get started.</p>
        </div>
      ) : (
        <div className="lib-grid">
          {uploadingRef && (
            <div className="lib-card" style={{ opacity: 0.85 }}>
              <div className="lib-hero">
                <span className="spin" />
                <span className="vc-tag">upload</span>
              </div>
              <div className="lib-body">
                <div className="lib-name">
                  <div className="n">{uploadingRef.name}</div>
                  <div className="m">uploading… · {Math.round(uploadingRef.duration)}s</div>
                </div>
                <div
                  style={{
                    height: 3,
                    background: "var(--bg-3)",
                    borderRadius: 2,
                    overflow: "hidden",
                    marginTop: "auto",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: "40%",
                      background: "linear-gradient(90deg, var(--accent), var(--accent-2))",
                      animation: "indeterminate 1.5s ease-in-out infinite",
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {visibleCards.map((v, idx) => {
            const isProvided = v._isProvided;
            const own = (v as { _own: boolean })._own;
            const editable = own || isAdmin;
            const cardKey = `${isProvided ? "p" : "o"}-${v.ownerUserId ?? "self"}-${v.name}`;
            const trimKey = isProvided ? `provided-${v.ownerUserId}-${v.name}` : v.name;
            const trimming = trimmingRef === trimKey;
            const trimAllowed = isProvided ? canTrimProvidedRef(v) : canTrimRef(v);
            const renaming = renamingRef === v.name;
            const status = v.voicePreviewStatus;
            const isCrossUser = !!v.provided && !!v.ownerUserId;
            const previewReady = status === "complete" && !!v.voicePreviewUrl;
            const clipLength = savedClipDuration(v);
            const retryKey = `${v.name}::${v.s3Key}`;
            const retrying = retryingPreview.has(retryKey);
            const generateStatus: "idle" | "queued" | "processing" | "failed" =
              status === "queued" || status === "processing"
                ? status
                : status === "failed"
                  ? "failed"
                  : "idle";

            return (
              <div
                key={cardKey}
                className={`lib-card${playingName === v.name ? " playing" : ""}`}
              >
                <div className="lib-hero">
                  {v.imageUrl ? (
                    // crossOrigin keeps the browser cache CORS-compatible so
                    // the cropper can later draw this image to a canvas without
                    // hitting a "tainted by cross-origin data" error.
                    <img src={v.imageUrl} alt="" crossOrigin="anonymous" />
                  ) : (
                    <span className="lib-hero-initials">{initials(v.name)}</span>
                  )}
                  {v.previewUrl && (
                    <button
                      type="button"
                      className="lib-hero-play"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlay(v);
                      }}
                      title={playingName === v.name ? "Pause" : "Play"}
                    >
                      {playingName === v.name ? <Icon.Pause /> : <Icon.Play />}
                    </button>
                  )}
                </div>
                <div className="lib-body">
                <div className="lib-row1">
                  <div className="lib-name">
                    {renaming ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => handleRenameKeyDown(e, v, isProvided ? v.ownerUserId : undefined)}
                          autoFocus
                          className="field-input"
                          style={{ padding: "4px 8px", fontSize: 13 }}
                          disabled={savingRename}
                        />
                        <button
                          type="button"
                          onClick={() => handleSaveRename(v, isProvided ? v.ownerUserId : undefined)}
                          className="btn btn-ghost btn-sm"
                          style={{ color: "var(--green)" }}
                        >
                          {savingRename ? "…" : "✓"}
                        </button>
                        <button type="button" onClick={handleCancelRename} className="btn btn-ghost btn-sm">
                          ✕
                        </button>
                      </div>
                    ) : (
                      <>
                        <div
                          className="n"
                          onClick={editable ? () => handleStartRename(v) : undefined}
                          style={{ cursor: editable ? "pointer" : "default" }}
                          title={editable ? "Click to rename" : undefined}
                        >
                          {v.name}
                        </div>
                        <div className="m">
                          {Math.round(v.duration)}s
                          {v.uploadedAt
                            ? ` · ${new Date(v.uploadedAt).toLocaleDateString()}`
                            : ""}
                        </div>
                      </>
                    )}
                  </div>
                  {!renaming && (
                    <span className={`vc-tag${isProvided ? " prov" : ""}`}>
                      {isProvided ? "provided" : "custom"}
                    </span>
                  )}
                </div>

                <div className="lib-foot">
                  {previewReady ? (
                    <VoicePreviewPlayer
                      audioUrl={v.voicePreviewUrl}
                      peaks={v.voicePreviewPeaks}
                      // Only backfill peaks for voices the user owns.
                      // Provided/cross-workspace voices skip backfill — the
                      // owner workspace will populate them on their next view.
                      backfillTarget={
                        v.provided
                          ? undefined
                          : { kind: "reference-voice-preview", refName: v.name }
                      }
                      playing={previewPlaying === v.name}
                      progress={previewProgress[v.name] ?? 0}
                      seed={idx}
                      onPlayPause={(e) => {
                        e.stopPropagation();
                        handleVoicePreviewPlay(v);
                      }}
                      onSeek={(fraction, e) => {
                        e.stopPropagation();
                        handleVoicePreviewSeek(v, fraction);
                      }}
                      onTimeUpdate={(el) => {
                        if (el.duration && Number.isFinite(el.duration)) {
                          setPreviewProgress((prev) => ({
                            ...prev,
                            [v.name]: Math.min(1, el.currentTime / el.duration),
                          }));
                        }
                      }}
                      onLoadedMetadata={() => {}}
                      onEnded={() => {
                        setPreviewPlaying((p) => (p === v.name ? null : p));
                        setPreviewProgress((prev) => ({ ...prev, [v.name]: 0 }));
                      }}
                      audioRef={(el) => {
                        previewAudios.current.set(v.name, el);
                      }}
                    />
                  ) : (
                    <GeneratePreviewButton
                      status={generateStatus}
                      retrying={retrying}
                      disabled={isCrossUser}
                      errorTitle={v.voicePreviewError}
                      onClick={() => handleGeneratePreview(v)}
                    />
                  )}
                  <div className="actions">
                    {(v.defaultClipPreviewUrl || v.previewUrl) && (
                      <button
                        type="button"
                        onClick={() => handleDownloadRef(v, cardKey)}
                        disabled={downloadingRef === cardKey}
                        title={
                          clipLength != null
                            ? `Download saved clip (${Math.round(clipLength)}s)`
                            : `Download original (${Math.round(v.duration)}s)`
                        }
                      >
                        {downloadingRef === cardKey ? (
                          <span className="spin" style={{ width: 13, height: 13 }} />
                        ) : (
                          <Icon.Download />
                        )}
                      </button>
                    )}
                    {editable && (
                      <button type="button" onClick={() => handleEditImage(v)} title="Image">
                        <Icon.Image />
                      </button>
                    )}
                    {trimAllowed && (
                      <button
                        type="button"
                        onClick={() => handleStartTrim(trimKey)}
                        title="Trim"
                      >
                        <Icon.Scissors />
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() =>
                          handleToggleProvided(
                            v.ownerUserId ?? user?.userId ?? "",
                            v.name,
                            !!v.provided
                          )
                        }
                        title={v.provided ? "Unmark as provided" : "Mark as provided"}
                        style={{ color: v.provided ? "var(--amber)" : undefined }}
                      >
                        <Icon.Star />
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() =>
                          handleOpenDefaults(
                            v.ownerUserId ?? user?.userId ?? "",
                            v.name,
                            v.defaults as VoiceDefaults | undefined
                          )
                        }
                        title="Voice defaults"
                      >
                        <Icon.Settings />
                      </button>
                    )}
                    {(!isProvided || isAdmin) && (
                      <button
                        type="button"
                        onClick={() => handleDelete(v.name)}
                        onBlur={() => {
                          if (confirmingDelete === v.name) setConfirmingDelete(null);
                        }}
                        title={confirmingDelete === v.name ? "Confirm delete" : "Delete"}
                        style={{
                          color:
                            confirmingDelete === v.name
                              ? "var(--red)"
                              : undefined,
                        }}
                      >
                        <Icon.Trash />
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline trim panel */}
                {trimming && v.previewUrl && (
                  <div
                    style={{
                      paddingTop: 12,
                      borderTop: "1px solid var(--hair)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    <WaveformTrimmer audioUrl={v.previewUrl} onTrim={handleTrimUpdate} />
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <p style={{ fontSize: 11.5, color: "var(--ink-3)", margin: 0 }}>
                        {trimRange
                          ? `Selected: ${(trimRange.end - trimRange.start).toFixed(1)}s`
                          : "Drag handles to select region"}
                      </p>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            setTrimmingRef(null);
                            setTrimRange(null);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className={`btn btn-primary btn-sm${
                            !trimRange || savingTrim ? " disabled" : ""
                          }`}
                          onClick={() => handleSaveTrim(v)}
                        >
                          {savingTrim ? "Saving…" : "Save trim"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Admin: All Users' References */}
      {isAdmin && (
        <div className="card" style={{ marginTop: 24, padding: 0, overflow: "hidden" }}>
          <button
            type="button"
            onClick={() => {
              setShowAllRefs(!showAllRefs);
              if (!showAllRefs) loadAllRefs();
            }}
            style={{
              width: "100%",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "transparent",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--amber)", fontSize: 13, fontWeight: 500 }}>
              <Icon.Users /> All users' references
            </span>
            <Icon.Caret
              width={14}
              height={14}
              style={{
                color: "var(--ink-3)",
                transform: showAllRefs ? "rotate(180deg)" : "none",
                transition: "transform .2s",
                flexShrink: 0,
              }}
            />
          </button>
          {showAllRefs && (
            <div
              style={{
                padding: "12px 16px",
                borderTop: "1px solid var(--hair)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {refOwnersDisplay.length === 0 ? (
                <p style={{ fontSize: 13, color: "var(--ink-3)", margin: 0 }}>
                  No references from other users.
                </p>
              ) : (
                refOwnersDisplay.map(({ email, userId, count }) => {
                  const refs = refsByUser[userId] ?? [];
                  const isLoading = loadingRefsFor.has(userId);
                  const isOpen = expandedRefGroups.has(email);
                  return (
                    <div
                      key={email}
                      style={{
                        border: "1px solid var(--hair)",
                        borderRadius: 10,
                        overflow: "hidden",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleRefGroup(email, userId)}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: "var(--bg-1)",
                        }}
                      >
                        <span style={{ fontSize: 12, color: "var(--ink-2)" }}>
                          {email}{" "}
                          <span style={{ color: "var(--ink-4)" }}>
                            ({count} {count === 1 ? "reference" : "references"})
                          </span>
                        </span>
                        <Icon.Caret
                          width={14}
                          height={14}
                          style={{
                            color: "var(--ink-3)",
                            transform: isOpen ? "rotate(180deg)" : "none",
                            transition: "transform .2s",
                            flexShrink: 0,
                          }}
                        />
                      </button>
                      {isOpen && (
                        <div
                          style={{
                            padding: 12,
                            borderTop: "1px solid var(--hair)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                          }}
                        >
                          {isLoading && refs.length === 0 ? (
                            <p style={{ fontSize: 12, color: "var(--ink-3)" }}>Loading…</p>
                          ) : refs.length === 0 ? (
                            <p style={{ fontSize: 12, color: "var(--ink-3)" }}>No references.</p>
                          ) : (
                            refs.map((ref) => (
                              <div
                                key={`${userId}-${ref.name}`}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  padding: "8px 10px",
                                  borderRadius: 8,
                                  background: "var(--bg-1)",
                                }}
                              >
                                <button
                                  type="button"
                                  className="vc-avatar"
                                  style={{ width: 28, height: 28, fontSize: 11, cursor: "pointer" }}
                                  onClick={() => handlePlay(ref)}
                                >
                                  {playingName === ref.name ? (
                                    <Icon.Pause style={{ width: 11, height: 11 }} />
                                  ) : (
                                    initials(ref.name)
                                  )}
                                </button>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      color: "var(--ink-2)",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {ref.name}
                                  </div>
                                  <div
                                    style={{
                                      fontFamily: "JetBrains Mono, monospace",
                                      fontSize: 10.5,
                                      color: "var(--ink-4)",
                                    }}
                                  >
                                    {Math.round(ref.duration)}s
                                  </div>
                                </div>
                                {ref.provided && <span className="pill ok">provided</span>}
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  onClick={() =>
                                    handleOpenDefaults(
                                      userId,
                                      ref.name,
                                      ref.defaults as VoiceDefaults | undefined
                                    )
                                  }
                                  title="Voice defaults"
                                >
                                  <Icon.Settings />
                                </button>
                                <button
                                  type="button"
                                  className={`btn btn-sm ${ref.provided ? "btn-line" : "btn-ghost"}`}
                                  onClick={() =>
                                    handleToggleProvided(userId, ref.name, !!ref.provided)
                                  }
                                >
                                  {ref.provided ? "Unmark" : "Mark provided"}
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* Voice Defaults Modal */}
      {defaultsTarget && (
        <div className="modal-scrim" onClick={() => setDefaultsTarget(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h3 className="modal-title">Voice defaults</h3>
                <div className="modal-sub">{defaultsTarget.name}</div>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => setDefaultsTarget(null)}
                aria-label="Close"
              >
                <Icon.X />
              </button>
            </div>

            <p style={{ fontSize: 12.5, color: "var(--ink-3)", margin: 0, lineHeight: 1.5 }}>
              Set default parameters for this voice. Users get these values pre-filled when
              selecting it.
            </p>

            <div className="stack-v">
              <ParamSlider
                label="Exaggeration"
                value={defaultsForm.exaggeration ?? 0.5}
                onChange={(v) => setDefaultsForm((p) => ({ ...p, exaggeration: v }))}
                min={0}
                max={1}
                step={0.05}
              />
              <ParamSlider
                label="CFG Weight"
                value={defaultsForm.cfgWeight ?? 0.5}
                onChange={(v) => setDefaultsForm((p) => ({ ...p, cfgWeight: v }))}
                min={0}
                max={1}
                step={0.05}
              />
              <ParamSlider
                label="Temperature"
                value={defaultsForm.temperature ?? 0.8}
                onChange={(v) => setDefaultsForm((p) => ({ ...p, temperature: v }))}
                min={0.1}
                max={1.5}
                step={0.05}
              />
              <ParamSlider
                label="Repetition Penalty"
                value={defaultsForm.repetitionPenalty ?? 1.1}
                onChange={(v) => setDefaultsForm((p) => ({ ...p, repetitionPenalty: v }))}
                min={1.0}
                max={2.0}
                step={0.05}
              />
              <ParamSlider
                label="Top P"
                value={defaultsForm.topP ?? 0.95}
                onChange={(v) => setDefaultsForm((p) => ({ ...p, topP: v }))}
                min={0.5}
                max={1.0}
                step={0.05}
              />
              <ParamSlider
                label="Min P"
                value={defaultsForm.minP ?? 0.05}
                onChange={(v) => setDefaultsForm((p) => ({ ...p, minP: v }))}
                min={0}
                max={0.5}
                step={0.01}
              />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", paddingTop: 6 }}>
              {defaultsTarget.current && (
                <button
                  type="button"
                  className={`btn btn-danger${savingDefaults ? " disabled" : ""}`}
                  onClick={handleClearDefaults}
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDefaultsTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn-primary${savingDefaults ? " disabled" : ""}`}
                onClick={handleSaveDefaults}
              >
                {savingDefaults ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image editor modal — keyed by ref name; we look up the live ref on
          render so a re-load (post-save) never strands stale URLs. */}
      {editingImageRef && (() => {
        const ref =
          references.find((r) => r.name === editingImageRef) ??
          providedRefs.find((r) => r.name === editingImageRef);
        if (!ref) return null;
        return (
          <ImageEditor
            initialOriginalUrl={ref.imageOriginalUrl ?? ref.imageUrl}
            initialCrop={ref.imageCrop}
            onCancel={handleCancelImage}
            onSaved={(result) => void handleImageEditorSaved(ref, result)}
            onError={(message) => setError(message)}
          />
        );
      })()}
    </div>
  );
}

